import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { Activity, ActivityAction } from "@/types/activity";

const channelCache = new Map<string, RealtimeChannel>();
const subscribedChannels = new Set<string>();

function ensureChannel(docId: string) {
  const cacheKey = `activity:${docId}`;
  
  if (!channelCache.has(docId)) {
    const channel = supabase.channel(cacheKey);
    channelCache.set(docId, channel);
  }
  
  return channelCache.get(docId)!;
}

export function normalizeActivityPayload(payload: Partial<Activity>): Activity {
  const fallbackAction: ActivityAction = "edited";
  const actor = payload.actor ?? { id: "unknown", name: "Someone" };
  const target = payload.target ?? { title: "an item" };

  return {
    id: payload.id || crypto.randomUUID(),
    actor,
    action: payload.action ?? fallbackAction,
    target,
    timestamp: payload.timestamp || new Date().toISOString(),
  };
}

export function subscribeToActivity(
  docId: string,
  onActivity: (activity: Activity) => void
): () => void {
  const channel = ensureChannel(docId);

  channel.on("broadcast", { event: "activity" }, ({ payload }) => {
    if (!payload) return;
    const normalized = normalizeActivityPayload(payload as Partial<Activity>);
    onActivity(normalized);
  });

  if (!subscribedChannels.has(docId)) {
    channel.subscribe();
    subscribedChannels.add(docId);
  }

  return () => {
    channel.unsubscribe();
    channelCache.delete(docId);
    subscribedChannels.delete(docId);
  };
}

export function emitActivity(docId: string, activity: Activity) {
  const channel = ensureChannel(docId);
  
  // Only send if channel is already subscribed, otherwise skip
  if (subscribedChannels.has(docId) && channel.state === 'joined') {
    channel.send({ type: "broadcast", event: "activity", payload: activity });
  }
}
